import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Lock, Users, MessageCircle, Zap, AlertCircle, Database, Globe, Smartphone, Clock, Baby } from 'lucide-react';
import SEOHead from './SEOHead';

const linkCls = "text-rose-600 hover:text-rose-700 underline font-medium";

const PrivacyPolicy = () => {
  const lastUpdated = "June 1, 2026";

  const sections = [
    {
      id: "information-we-collect",
      title: "1. Information We Collect",
      icon: <Eye className="w-5 h-5" />,
      content: [
        {
          subtitle: "1.1 Information You Provide",
          items: [
            "Account data: email address, password (stored hashed with bcrypt), username, display name, date of birth (for age verification), gender, sexual orientation, and the gender(s) you want to match with",
            "Profile data: photos, bio, interests, occupation, education, height, lifestyle preferences, and any other content you choose to add",
            "Verification data: if you choose to verify your account, we may collect additional photos or documents to confirm identity",
            "Payment data: our payment processor (Stripe) collects your card details. We never store full card numbers — only the last 4 digits, card brand, and billing ZIP",
            "Communications: messages, chat content, icebreakers, and feedback you send through the Service or to our support team",
            "Reports: if you report another user or raise a safety concern, we store your report details to investigate"
          ]
        },
        {
          subtitle: "1.2 Information Collected Automatically",
          items: [
            "Precise location: with your permission, we collect your device's GPS coordinates to show nearby matches. You can disable this anytime in your device settings; distance-based matching will be unavailable",
            "Device data: device model, operating system version, mobile network, language, time zone, and a unique device identifier",
            "Usage data: pages visited, features used, matches made, message counts, session duration, and interaction timestamps",
            "Log data: IP address, access times, crash reports, and performance metrics",
            "Cookies and similar technologies on our website (not in the mobile app) for session management and analytics"
          ]
        },
        {
          subtitle: "1.3 Information from Third Parties",
          items: [
            "Social sign-in: if you sign in with Google, we receive your name, email, and profile picture from Google (subject to your Google privacy settings)",
            "Payment processors: Stripe shares subscription status, charge success/failure, and refund events with us",
            "Video calls: Twilio provides session duration, connection quality metrics, and participant counts. Twilio does not record the content of your video or audio, and neither do we"
          ]
        }
      ]
    },
    {
      id: "how-we-use-information",
      title: "2. How We Use Your Information",
      icon: <Zap className="w-5 h-5" />,
      content: [
        {
          subtitle: "Core Uses",
          items: [
            "Create and manage your account",
            "Match you with other users based on your preferences, location, and compatibility signals",
            "Facilitate speed-dating video sessions, chat, and other communications",
            "Process payments and manage your subscription",
            "Send transactional messages (match notifications, chat alerts, billing receipts) via push, email, or SMS based on your preferences",
            "Detect, prevent, and respond to fraud, abuse, harassment, and policy violations",
            "Verify that users are 18 or older",
            "Improve the Service, debug errors, develop new features, and comply with legal obligations"
          ]
        },
        {
          subtitle: "AI Features",
          items: [
            "We use AI models (OpenAI, Anthropic Claude, Google Gemini) to power AI coaching, profile suggestions, and conversation icebreakers",
            "When you use these features, the text you submit may be sent to the AI provider to generate a response",
            "We do not share your photos or video with AI providers",
            "We do not use your personal data to train third-party AI models"
          ]
        }
      ]
    },
    {
      id: "information-sharing",
      title: "3. How We Share Your Information",
      icon: <Users className="w-5 h-5" />,
      content: [
        {
          subtitle: "3.1 With Other Users",
          items: [
            "Your profile (photos, bio, display name, approximate distance, age) is visible to users who match your search criteria",
            "We never show your exact address, email, phone number, or last name",
            "Messages are visible only to the recipient, and to our moderation team if reported"
          ]
        },
        {
          subtitle: "3.2 With Service Providers",
          items: [
            "MongoDB Atlas — database hosting",
            "Stripe — payment processing",
            "Twilio — video calling and SMS",
            "SendGrid — transactional email",
            "Google (OAuth) — authentication",
            "OpenAI, Anthropic, Google (Gemini) — AI features",
            "PostHog — product analytics (anonymized where possible)",
            "Cloudflare — content delivery and DDoS protection",
            "Each provider is contractually required to protect your data and use it only for the service they provide to us"
          ]
        },
        {
          subtitle: "3.3 For Safety and Legal Reasons",
          items: [
            "Comply with a subpoena, court order, or other legal process",
            "Investigate fraud, harassment, or threats to users",
            "Protect our rights, property, or safety, or that of our users or the public",
            "Respond to a government request or emergency"
          ]
        },
        {
          subtitle: "3.4 Business Transfers",
          items: [
            "If Shot Clock is acquired, merged, or sells its assets, your information may be transferred to the acquiring entity under the same privacy commitments"
          ]
        },
        {
          subtitle: "3.5 We Do Not Sell Your Personal Information",
          items: [
            "We do not sell personal data to third parties",
            "We do not share your data with advertising networks for targeted advertising outside the Service"
          ]
        }
      ]
    },
    {
      id: "your-rights",
      title: "4. Your Choices and Rights",
      icon: <Shield className="w-5 h-5" />,
      content: [
        {
          subtitle: "Control Your Data",
          items: [
            "Access and correction: view and edit your profile data in-app at any time",
            <>Delete your account: go to Settings → Account → Delete Account, or visit <a href="/delete-account" className={linkCls} data-testid="privacy-delete-account-link">shotclockdating.com/delete-account</a>. Deletion is permanent and removes your profile, matches, and messages from active storage within 30 days. Some data may be kept longer for fraud prevention, legal compliance, or in anonymized form</>,
            <>Download your data: email <a href="mailto:privacy@shotclockdating.com" className={linkCls}>privacy@shotclockdating.com</a> to request a copy</>,
            "Push notifications: manage from Settings → Notifications or your device OS",
            "Location: turn off GPS access in your device settings (distance-based matching will be disabled)",
            "Marketing emails: unsubscribe from any marketing email, or disable them in Settings"
          ]
        },
        {
          subtitle: "Regional Rights (CCPA/CPRA, GDPR, LGPD, PIPEDA)",
          items: [
            "Residents of California, the EEA/UK, Brazil, and Canada have additional rights including access, portability, deletion, correction, restriction of processing, and withdrawal of consent",
            <>Contact <a href="mailto:privacy@shotclockdating.com" className={linkCls}>privacy@shotclockdating.com</a> with the subject line "Data Request". We respond within 30 days</>
          ]
        }
      ]
    },
    {
      id: "data-retention",
      title: "5. Data Retention",
      icon: <Clock className="w-5 h-5" />,
      content: [
        {
          subtitle: "How Long We Keep Data",
          items: [
            "Active account: retained for as long as your account is active",
            "Deleted account: profile data is removed within 30 days. Messages you sent remain in recipients' inboxes unless they delete them",
            "Safety and legal: reports, blocks, and bans are retained for up to 7 years to prevent repeat offenders",
            "Payment records: retained for 7 years per tax and accounting law",
            "Backups: encrypted backups are rotated within 90 days"
          ]
        }
      ]
    },
    {
      id: "data-security",
      title: "6. Security",
      icon: <Lock className="w-5 h-5" />,
      content: [
        {
          subtitle: "Safeguards",
          items: [
            "Passwords hashed with bcrypt",
            "TLS 1.2+ for all data in transit",
            "Encryption at rest for the production database",
            "Role-based access control for staff and regular security reviews",
            "No system is 100% secure. If a data breach affects you, we will notify you and the relevant authorities as required by law"
          ]
        }
      ]
    },
    {
      id: "children",
      title: "7. Children",
      icon: <Baby className="w-5 h-5" />,
      content: [
        {
          subtitle: "18+ Only",
          items: [
            "The Service is not intended for anyone under 18. We verify age at sign-up through date of birth",
            "If we learn that someone under 18 has created an account, we delete it immediately",
            <>If you believe a minor is using the Service, email <a href="mailto:privacy@shotclockdating.com" className={linkCls}>privacy@shotclockdating.com</a></>
          ]
        }
      ]
    },
    {
      id: "international",
      title: "8. International Transfers",
      icon: <Globe className="w-5 h-5" />,
      content: [
        {
          subtitle: "Where Data Is Processed",
          items: [
            "Shot Clock is operated from the United States. If you use the Service from outside the US, your data is transferred to and processed in the US",
            "For users in the EEA/UK, we rely on Standard Contractual Clauses for international transfers"
          ]
        }
      ]
    },
    {
      id: "app-store-disclosures",
      title: "Appendix — App Store Disclosures",
      icon: <Smartphone className="w-5 h-5" />,
      content: [
        {
          subtitle: "Apple App Privacy — Data Linked to You",
          items: [
            "Contact Info — Email, Name",
            "User Content — Photos, Messages, Other User Content",
            "Identifiers — User ID",
            "Usage Data — Product Interaction",
            "Diagnostics — Crash Data, Performance Data",
            "Location — Precise Location (optional)",
            "Purchases — Purchase History",
            "Sensitive Info — Sexual Orientation (optional, user-provided)",
            "Data used to track you: None. We do not use third-party advertising trackers"
          ]
        },
        {
          subtitle: "Google Play Data Safety",
          items: [
            "Data collected: Email, Name, Photos, Messages, Precise Location (optional), Device ID, Purchase History, App interactions, Crash logs",
            "Data shared with third parties: Payment data (Stripe), video session metadata (Twilio), analytics (PostHog, anonymized where possible)",
            "Data is encrypted in transit: Yes",
            "Users can request data deletion: Yes (in-app, at /delete-account, and via email)"
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 overflow-x-hidden" data-testid="privacy-policy-page">
      <SEOHead 
        title="Privacy Policy | Shot Clock Dating"
        description="Learn how Shot Clock Dating collects, uses, shares, and protects your information across our website and iOS/Android apps."
        keywords="privacy policy, data protection, AI dating privacy, video call privacy, account deletion, data safety"
        url="https://shotclockdating.com/privacy-policy"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 to-pink-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            className="flex items-center justify-center mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Shield className="w-12 h-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
          </motion.div>
          <motion.p 
            className="text-xl text-rose-100 mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            How we protect your information on web, iOS, and Android
          </motion.p>
          <motion.p 
            className="text-rose-200"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            data-testid="privacy-last-updated"
          >
            Effective & Last Updated: {lastUpdated}
          </motion.p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Introduction */}
        <motion.div 
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <div className="flex items-center mb-6">
            <div className="w-6 h-6 mr-3 flex items-center justify-center">
              <img 
                src="/shot-clock-icon-privacy.png" 
                alt="Shot Clock Dating Icon"
                className="w-6 h-6 object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Welcome to Shot Clock Dating</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">
            Shot Clock Dating ("Shot Clock," "we," "us," or "our") operates the Shot Clock Dating mobile application and the website at shotclockdating.com (together, the "Service"). This Privacy Policy explains how we collect, use, share, and protect your information when you use the Service.
          </p>
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-4" data-testid="privacy-age-notice">
            <p className="text-rose-800 font-semibold">
              You must be 18 years or older to use Shot Clock Dating. We do not knowingly collect information from anyone under 18.
            </p>
          </div>
        </motion.div>

        {/* Main Sections */}
        {sections.map((section, index) => (
          <motion.div 
            key={section.id}
            id={section.id}
            data-testid={`privacy-section-${section.id}`}
            className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 + (index * 0.05), duration: 0.8 }}
          >
            <div className="flex items-center mb-6">
              <div className="text-rose-600 mr-3">{section.icon}</div>
              <h2 className="text-2xl font-bold text-gray-800">{section.title}</h2>
            </div>
            
            {section.content.map((subsection, subIndex) => (
              <div key={subIndex} className="mb-6 last:mb-0">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">{subsection.subtitle}</h3>
                <ul className="space-y-2">
                  {subsection.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <div className="w-2 h-2 bg-rose-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </motion.div>
        ))}

        {/* Contact & Updates */}
        <motion.div 
          className="bg-gradient-to-r from-rose-100 to-pink-100 rounded-xl p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8 }}
        >
          <div className="flex items-center mb-6">
            <MessageCircle className="w-6 h-6 text-rose-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-800">9. Changes & 10. Contact Us</h2>
          </div>
          <p className="text-gray-700 mb-4">
            We may update this Privacy Policy from time to time. If we make material changes, we will notify you in-app and by email at least 7 days before they take effect. Continued use of the Service after the effective date means you accept the updated policy.
          </p>
          <div className="bg-white rounded-lg p-4 border border-rose-200" data-testid="privacy-contact-box">
            <p className="text-gray-700 font-semibold mb-2">Shot Clock Dating — Privacy Officer</p>
            <p className="text-gray-700">
              <strong>Privacy:</strong>{' '}
              <a href="mailto:privacy@shotclockdating.com" className={linkCls} data-testid="privacy-email-link">privacy@shotclockdating.com</a>
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Support:</strong>{' '}
              <a href="mailto:support@shotclockdating.com" className={linkCls}>support@shotclockdating.com</a>
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Website:</strong> https://shotclockdating.com
            </p>
          </div>
        </motion.div>

        {/* Important Notice */}
        <motion.div 
          className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mt-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.8 }}
        >
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-yellow-600 mr-3 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-yellow-800 mb-2 flex items-center"><Database className="w-4 h-4 mr-2" />Important</h3>
              <p className="text-yellow-700 text-sm leading-relaxed">
                By using Shot Clock Dating, you acknowledge that you have read, understood, and agree to this Privacy Policy. 
                If you do not agree with our privacy practices, please do not use our services.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
